import { useAvailablePromoCodesList, useDefaultPaymentPlanId } from '@if/core'
import { EFinancingProgram, EProvince } from '@if/core/types'
import {
  BaseWorksheetFormConfig,
  buildBaseWorksheetFormConfig,
  useResolvedFinancingConfig,
} from '@src/components/WorksheetForm'
import { useBaseWorksheetFormInitialValues } from '@src/components/WorksheetForm/BaseWorksheetForm.schema'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { Constants } from '@src/data/types'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { Merchant } from '@src/data/types/Merchant'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'
import { CalculatorValues, useCalculatorForm } from '../calculatorSchema'

export interface UseCalculatorFormSetupProps {
  creditApplicationId: string | null
  financingProgramId: EFinancingProgram | null
  merchantId: string | null
  expiresOn: Date | string | null
  createdOn: Date | null
  decisionInterestRate: number | null
  maxAmountFinanced: number | null
  maxTermDuration: number | null
  maxPmtAmount?: number
  hasCoApplicant: boolean
  stateIso: EProvince | null
  normsCreditDecision: NormCreditDecision | null
}

export interface CalculatorFormSetup {
  // Non-null once merchant + financing config are loaded and the essential
  // identifiers (financingProgramId, stateIso, dates) are present.
  config: BaseWorksheetFormConfig | null
  interestRate: number
  merchant: Merchant | null
  availablePromoCodes: Set<string>
}

// Data prep only — does NOT build the form schema. Schema construction is
// gated behind `config != null` and runs in `useCalculatorFormFields`, which
// callers invoke only once they're rendering a step that needs the form
// (typically inside MultiFormStep's `useFormSetup`, or inside a child component
// that mounts after the prep is ready).
export const useCalculatorFormSetup = ({
  creditApplicationId,
  financingProgramId,
  merchantId,
  expiresOn,
  createdOn,
  decisionInterestRate,
  maxAmountFinanced,
  maxTermDuration,
  maxPmtAmount,
  hasCoApplicant,
  stateIso,
  normsCreditDecision,
}: UseCalculatorFormSetupProps): CalculatorFormSetup => {
  const [merchant] = useGetMerchantById(merchantId ?? undefined)

  const programConfig =
    (financingProgramId && Constants.financingProgramConfigs[financingProgramId]) ||
    Constants.financingProgramConfigs.default

  const { financingConfig } = useResolvedFinancingConfig(financingProgramId ?? undefined, merchantId ?? undefined)

  const interestRate = decisionInterestRate ?? programConfig.minInterestRate

  const availablePromoCodes = useAvailablePromoCodesList(
    merchant?.planPromoRules,
    normsCreditDecision,
    // Sentinel — when financingProgramId is null `config` is null below so the
    // returned set never reaches a real consumer.
    financingProgramId ?? EFinancingProgram.Personal,
  )

  const config = useMemo<BaseWorksheetFormConfig | null>(() => {
    if (!financingProgramId || !stateIso || !financingConfig || !createdOn || !expiresOn) {
      return null
    }
    return buildBaseWorksheetFormConfig({
      financingProgramId,
      creditApplicationId: creditApplicationId ?? nanoid(),
      merchantId: merchant?.id ?? '',
      financingConfig,
      stateIso,
      hasCoApplicant,
      interestRate,
      maxPmtAmount,
      createdOn,
      expiresOn,
      merchant,
      normsCreditDecision,
      maxAmountFinanced,
      maxTermDuration,
      isSinglePayment: false,
      availablePromoCodes,
    })
  }, [
    financingProgramId,
    creditApplicationId,
    merchant,
    financingConfig,
    stateIso,
    hasCoApplicant,
    interestRate,
    maxPmtAmount,
    createdOn,
    expiresOn,
    normsCreditDecision,
    maxAmountFinanced,
    maxTermDuration,
    availablePromoCodes,
  ])

  return {
    config,
    interestRate,
    merchant,
    availablePromoCodes,
  }
}

export interface UseCalculatorFormFieldsArgs {
  config: BaseWorksheetFormConfig
  availablePromoCodes: Set<string>
  worksheet: BaseWorksheetDto | null
  requestedLoanAmount: number | null
}

export interface CalculatorFormFields {
  resolver: Resolver<CalculatorValues>
  defaultValues: CalculatorValues | undefined
}

// Schema/resolver/initial-values builder. Only call this with a non-null config
// — callers gate on `setup.config != null` (e.g. via MultiFormStep's
// `useFormSetup` hook, which only fires while the step is active+ready, or by
// rendering a child component that mounts after prep is ready).
export const useCalculatorFormFields = ({
  config,
  availablePromoCodes,
  worksheet,
  requestedLoanAmount,
}: UseCalculatorFormFieldsArgs): CalculatorFormFields => {
  const defaultPaymentPlanId = useDefaultPaymentPlanId(config.availablePaymentPlans, availablePromoCodes)
  const { resolver, defaultValues } = useCalculatorForm(config)
  const initialValues = useBaseWorksheetFormInitialValues(
    defaultValues,
    worksheet,
    requestedLoanAmount,
    defaultPaymentPlanId,
  )
  return { resolver, defaultValues: initialValues }
}
